import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Router } from '@angular/router';
import {
    ConfirmationWindowComponent,
    ConfirmationWindowTypes,
} from '@components/confirmation-window/confirmation-window.component';
import { NewGraphDialogComponent } from '@components/graphs/new-graph-dialog/new-graph-dialog.component';
import { GraphsSocketService } from '@service/graphs/graphs-socket.service';
import { GraphsService } from '@service/graphs/graphs.service';
import { VesselService } from '@service/vessel.service';
import { Graph, GraphType, MAX_GRAPHS, GraphTypeTranslate } from 'icreate/common/models/graphs';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { PrintGraphsService } from '@service/graphs/print-graphs.service';
import { PullProjectService } from '@service/pull-project.service';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { EditGraphDialogComponent } from '@components/graphs/edit-graph-dialog/edit-graph-dialog.component';
import { MessageDialogService } from '@service/message-dialog.service';

@Component({
    selector: 'app-graphs',
    templateUrl: './graphs.component.html',
    styleUrl: './graphs.component.css',
    standalone: false,
})
export class GraphsComponent implements OnInit, OnDestroy {
    readonly MAX_GRAPHS = MAX_GRAPHS;

    @ViewChild('tabListElement') tabListElement: ElementRef;
    @ViewChild('optionsContainer') optionsContainer: ElementRef;
    @ViewChild('deleteConfirmation') deleteConfirmation: ConfirmationWindowComponent;
    @ViewChild('cancelConfirmation') cancelConfirmation: ConfirmationWindowComponent;
    @ViewChild('dropdownWrapper') dropdownWrapper!: ElementRef;

    @Input() vessel: Vessel;
    @Input() userCanEdit: boolean;

    closeGraph$: Subject<void> = new Subject<void>();
    saveGraph$: Subject<void> = new Subject<void>();
    saveCompleted$ = new Subject<void>();
    saveIsEnabled: boolean = false;
    graphTypeMap: Map<GraphType, string> = new Map(
        GraphTypeTranslate.map(item => [item.type, this.translateService.instant(item.translate)])
    );
    graphTypeList: any[];

    openOptionsGraphId: number | null = null;

    graphList: Graph[] = null;
    selectedGraph: Graph;
    deleteGraphTitle: string;

    showLoadingSpinner: boolean = false;

    dynamicHeight: number;
    elementHeight: number;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private vesselService: VesselService,
        private router: Router,
        private dialog: MatDialog,
        private graphsService: GraphsService,
        private graphsSocketService: GraphsSocketService,
        private cdr: ChangeDetectorRef,
        private translateService: TranslateService,
        private printGraphsService: PrintGraphsService,
        private pullService: PullProjectService,
        private messageDialogService: MessageDialogService
    ) {}

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        const target = event.target as HTMLElement;

        if (!target.closest('.options')) {
            this.openOptionsGraphId = null;
        }
    }

    async ngOnInit() {
        this.graphsService.graphsLoadingEmitter
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(showSpinner => {
                this.showLoadingSpinner = showSpinner;
                this.cdr.detectChanges();
            });

        this.graphsService.graphList$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(updatedList => {
            this.graphList = updatedList;
        });

        this.graphsSocketService.startSubscriptions();

        this.graphsService.showLoadingSpinner(true);
        this.vesselService.accessUpdated$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(canEdit => {
            this.userCanEdit = canEdit;
        });

        this.graphList = await this.graphsService.getAll(this.vessel.id);

        if (this.graphList.length > 0) this.selectedGraph = this.graphList.at(0);

        this.graphsService.showLoadingSpinner(false);
        this.graphTypeList = Array.from(this.graphTypeMap.entries()).reverse();
    }

    ngOnDestroy(): void {
        this.graphsSocketService.destroySubscriptions();
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    async setGraph(graph: Graph) {
        if (this.saveIsEnabled && graph != null) {
            this.cancelConfirmation.setVisible(true);
            let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.save();
            }
        }
        this.saveIsEnabled = false;
        this.selectedGraph = graph;
    }

    async addGraphClicked() {
        if (!this.canAddGraphs) return;
        const dialogRef = this.dialog.open(NewGraphDialogComponent, {
            disableClose: true,
            width: '474px',
            height: '284px',
            data: { graphList: this.graphList, vessel: this.vessel },
        });
        const newGraph = await lastValueFrom(dialogRef.afterClosed());
        if (newGraph) {
            this.selectedGraph = newGraph;
            const dialogRef = this.dialog.open(EditGraphDialogComponent, {
                disableClose: true,
                width: '1200px',
                height: '660px',
                data: { graph: newGraph, vessel: this.vessel, isEditing: false },
            });
            const graph = await lastValueFrom(dialogRef.afterClosed());
            if (graph) {
                this.selectedGraph = graph;
                this.save();
            }
            this.messageDialogService.openMessage('graphs.message-dialog.create.message', {
                title: this.selectedGraph.title,
            });
        }
    }

    async graphDrop(event: CdkDragDrop<Graph[]>) {
        if (event.previousIndex === event.currentIndex) return;

        const previousOrder = [...this.graphList];
        moveItemInArray(this.graphList, event.previousIndex, event.currentIndex);

        const orderedIds = this.graphList.map(graph => graph.id);
        try {
            await this.graphsService.reorderGraphs(this.vessel.id, orderedIds);
        } catch (error) {
            console.log(error);
            this.graphList = previousOrder;
        }
    }

    async goBack() {
        if (this.saveIsEnabled) {
            this.cancelConfirmation.setVisible(true);
            let confirmationOption = await lastValueFrom(this.cancelConfirmation.onOptionSelected.pipe(take(1)));
            if (confirmationOption === ConfirmationWindowTypes.DISCARD) {
                this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
            } else if (confirmationOption === ConfirmationWindowTypes.GO_BACK) {
                return;
            } else if (confirmationOption === ConfirmationWindowTypes.SAVE) {
                this.save();
                this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
            }
        } else {
            this.router.navigateByUrl(`home/${this.vessel.shipyardId}/${this.vessel.id}`);
        }
    }

    isExhaustGraph() {
        if (!this.selectedGraph) return false;
        return this.selectedGraph.type === GraphType.Exhaust;
    }

    isBarGraph() {
        if (!this.selectedGraph) return false;
        return this.selectedGraph.type === GraphType.Bar;
    }

    isGaugeGraph() {
        if (!this.selectedGraph) return false;
        return this.selectedGraph.type === GraphType.Gauge;
    }

    tabContextMenu(event: MouseEvent) {
        event.preventDefault();
        event.stopPropagation();
        if (!this.tabListElement?.nativeElement.contains(event.target)) return;
        const elements = this.tabListElement.nativeElement.getElementsByClassName('tab');
        const nodes = Array.prototype.slice.call(elements);
        if (!nodes) return;
        const index = nodes.indexOf(event.target);
        if (index < 0) return;
        const graph = this.graphList[index];

        this.dynamicHeight = undefined;
        this.elementHeight = undefined;
        this.elementHeight = (event.target as any)?.getBoundingClientRect()?.top + 20;
        if (this.elementHeight) {
            this.dynamicHeight = this.elementHeight;
        }
    }

    onScroll(event: Event): void {
        if (!this.elementHeight) return;
        const target = event.target as HTMLElement;
        if (target && target.scrollTop) this.dynamicHeight = this.elementHeight - target.scrollTop;
    }

    async deleteGraph(graph: Graph) {
        if (!this.isGraphPageEditable) return;
        this.openOptionsGraphId = null;
        this.deleteGraphTitle = graph.title;

        this.deleteConfirmation.setVisible(true);
        let confirmationOption = await lastValueFrom(this.deleteConfirmation.onOptionSelected.pipe(take(1)));
        if (confirmationOption === ConfirmationWindowTypes.SAVE) {
            this.graphsService.showLoadingSpinner(true);
            try {
                await this.graphsService.deleteGraph(graph.id);
                if (this.selectedGraph.id == graph.id) {
                    this.saveIsEnabled = false;
                    this.selectedGraph = null;
                }
            } catch (err) {
                console.log(err);
            } finally {
                this.graphsService.showLoadingSpinner(false);
            }
        }
        this.deleteGraphTitle = undefined;
    }

    async cancel() {
        this.closeGraph$.next();
    }

    async save() {
        if (!this.saveIsEnabled || !this.isGraphPageEditable) return;
        this.saveGraph$.next();
    }

    getGraphTypeName = (graphType: GraphType) => {
        return this.graphTypeMap.get(graphType) || '';
    };

    toggleOptions(graphId: number, event: MouseEvent) {
        if (this.openOptionsGraphId === graphId) {
            this.openOptionsGraphId = null;
        } else {
            this.openOptionsGraphId = graphId;
        }
    }

    closeOptions() {
        this.openOptionsGraphId = null;
    }

    async editGraph(graph: Graph) {
        this.openOptionsGraphId = null;
        const dialogRef = this.dialog.open(EditGraphDialogComponent, {
            disableClose: true,
            width: '1200px',
            height: '660px',
            data: { graph: graph, vessel: this.vessel, isEditing: true },
        });
        const editedGraph = await lastValueFrom(dialogRef.afterClosed());
        if (editedGraph) {
            graph = editedGraph;
            this.messageDialogService.openMessage('graphs.message-dialog.update.message', {
                title: graph.title,
            });
        }
    }

    get canAddGraphs() {
        return this.graphList.length < MAX_GRAPHS && this.userCanEdit;
    }
    get isGraphPageEditable(): boolean {
        return this.userCanEdit;
    }
}
